import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Mail, Send, CheckCircle, X } from "lucide-react";

export default function Newsletter({ currentSection }) {
  const [email, setEmail] = useState("");
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;

    setIsLoading(true);

    try {
      const response = await fetch('https://formsubmit.co/ajax/marketing@lg-mi.it', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify({
          _subject: 'Nuova iscrizione Newsletter ELLEGI',
          _captcha: 'false',
          _template: 'table',
          email: email,
          tipo: 'Iscrizione Newsletter',
        }),
      });

      const result = await response.json();

      if (result.success === 'true' || result.success === true) {
        setIsSubmitted(true);
        setEmail('');
        setTimeout(() => {
          setIsSubmitted(false);
          setIsModalOpen(false);
        }, 3000);
      } else {
        alert('Iscrizione non riuscita. Riprova più tardi.');
      }
    } catch (error) {
      console.error('Errore iscrizione newsletter:', error);
      alert('Errore di rete. Controlla la connessione e riprova.');
    } finally {
      setIsLoading(false);
    }
  };

  const openModal = () => {
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setIsSubmitted(false);
    setEmail("");
  };

  const isHero = currentSection === "hero";

  return (
    <>
      {/* Newsletter Button - Hidden on hero section */}
      <AnimatePresence>
        {!isHero && (
          <motion.button
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            transition={{ duration: 0.4 }}
            onClick={openModal}
            className="fixed right-6 bottom-6 md:right-8 md:bottom-8
                       z-40 p-0 md:p-4 rounded-xl md:rounded-2xl md:glass md:border md:border-[--accent]/20 md:shadow-luxury hover:shadow-xl transition-all group"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <div className="flex items-center md:space-x-3">
              <div className="w-12 h-12 md:w-10 md:h-10 rounded-xl bg-gradient-to-br from-[--accent] to-yellow-400 flex items-center justify-center shadow-lg md:shadow-none">
                <Mail className="w-6 h-6 md:w-5 md:h-5 text-white" />
              </div>
              <div className="text-left hidden md:block">
                <h3 className="text-sm font-display font-semibold text-[--text-primary] group-hover:text-[--accent] transition-colors">
                  Newsletter
                </h3>
                <p className="text-xs text-[--text-secondary]">
                  Resta aggiornato
                </p>
              </div>
            </div>
          </motion.button>
        )}
      </AnimatePresence>

      {/* Fullscreen Modal */}
      <AnimatePresence>
        {isModalOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-[200] bg-black/80 backdrop-blur-sm flex items-center justify-center p-6"
            onClick={closeModal}
          >
            <motion.div
              initial={{ scale: 0.8, opacity: 0, y: 50 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.8, opacity: 0, y: 50 }}
              transition={{ duration: 0.4, type: "spring", stiffness: 100 }}
              className="glass rounded-3xl p-8 md:p-12 w-full max-w-md border border-[--accent]/20 shadow-luxury relative"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Close Button */}
              <button
                onClick={closeModal}
                className="absolute top-6 right-6 w-10 h-10 rounded-full bg-[--bg-secondary] hover:bg-[--accent] flex items-center justify-center transition-colors group"
              >
                <X className="w-5 h-5 text-[--text-primary] group-hover:text-white" />
              </button>

              {/* Header */}
              <div className="text-center mb-8">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[--accent] to-yellow-400 flex items-center justify-center mx-auto mb-4">
                  <Mail className="w-8 h-8 text-white" />
                </div>
                <h2 className="text-2xl md:text-3xl font-display font-bold text-[--text-primary] mb-2">
                  Newsletter ELLEGI
                </h2>
                <p className="text-[--text-secondary] text-lg">
                  Resta aggiornato sulle ultime novità del mondo dell'illuminazione
                </p>
              </div>

              {/* Success State */}
              {isSubmitted ? (
                <motion.div
                  initial={{ scale: 0.8, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  className="text-center py-8"
                >
                  <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-[--text-primary] mb-2">
                    Grazie per l'iscrizione!
                  </h3>
                  <p className="text-[--text-secondary]">
                    Ti terremo aggiornato sulle novità del settore illuminotecnico
                  </p>
                </motion.div>
              ) : (
                /* Form */
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="relative">
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="Inserisci la tua email"
                      required
                      className="w-full px-6 py-4 bg-[--bg-primary] border border-gray-300 dark:border-gray-600 rounded-xl text-[--text-primary] placeholder-[--text-secondary] focus:outline-none focus:ring-2 focus:ring-[--accent] focus:border-transparent transition-all text-lg"
                    />
                  </div>

                  <motion.button
                    type="submit"
                    disabled={isLoading || !email}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className="w-full bg-gradient-to-r from-[--accent] to-yellow-400 text-white py-4 px-6 rounded-xl font-semibold flex items-center justify-center space-x-2 hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed text-lg"
                  >
                    {isLoading ? (
                      <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <>
                        <span>Iscriviti alla Newsletter</span>
                        <Send className="w-5 h-5" />
                      </>
                    )}
                  </motion.button>

                  {/* Benefits */}
                  <div className="space-y-3 pt-4">
                    <p className="text-sm font-medium text-[--text-primary] mb-2">
                      Cosa riceverai:
                    </p>
                    <ul className="space-y-2 text-sm text-[--text-secondary]">
                      <li className="flex items-center">
                        <div className="w-2 h-2 bg-[--accent] rounded-full mr-3"></div>
                        Novità sui prodotti e tecnologie
                      </li>
                      <li className="flex items-center">
                        <div className="w-2 h-2 bg-[--accent] rounded-full mr-3"></div>
                        Inviti a eventi e fiere del settore
                      </li>
                      <li className="flex items-center">
                        <div className="w-2 h-2 bg-[--accent] rounded-full mr-3"></div>
                        Consigli e best practices
                      </li>
                    </ul>
                  </div>
                </form>
              )}

              {/* Privacy note */}
              <p className="text-xs text-[--text-secondary] mt-6 text-center">
                Rispettiamo la tua privacy. Nessuno spam. Puoi disiscriverti in qualsiasi momento.
              </p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
